import * as Sentry from '@sentry/react';
import { AdsAccount } from '@superfunnel/types';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Checkbox from '@superfunnel/ui-core/v2/Checkbox';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { approveConnectRequest } from '../services/AdsAccountsService';
import { capitalize, classNames, emptyConverter } from '../utils';
interface Props {
  requestId: string;
  provider: 'google' | 'facebook';
  open: boolean;
  onClose: () => void;
}

export default function ConnectAdsAccountsModal({
  requestId,
  provider,
  open,
  onClose,
}: Props) {
  const navigate = useNavigate();

  const { selectedAccount, refetchAccounts } = useContext(UserContext);

  const [accounts, setAccounts] = useState<AdsAccount[]>();
  const [selected, setSelected] = useState<{ [key: string]: boolean }>({});
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (open) return;

    setSelected({});
    setConnecting(false);
    setError(undefined);
  }, [open]);

  useEffect(() => {
    if (!requestId || !selectedAccount) {
      setAccounts(undefined);
      return;
    }

    const adsAccountsColRef = collection(
      selectedAccount.ref,
      'adsAccounts',
    ).withConverter(emptyConverter<AdsAccount>());

    const unsubscribe = onSnapshot(
      query(adsAccountsColRef, where('latestReqId', '==', requestId)),
      (accounts) => {
        const newAccounts = accounts.docs.map((d) => d.data());
        newAccounts.sort((a, b) => (a.status < b.status ? 1 : -1));
        setAccounts(newAccounts);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [requestId, selectedAccount]);

  const connect = useCallback(async () => {
    if (!selectedAccount) return;
    if (connecting) return;

    setConnecting(true);

    try {
      await approveConnectRequest(
        selectedAccount.id,
        requestId,
        Object.entries(selected)
          .filter((s) => s[1])
          .map((s) => s[0]),
      );

      refetchAccounts();
      onClose();
      navigate(
        `/workspace/${selectedAccount.data().slug}/settings/ad-accounts`,
      );
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setConnecting(false);
  }, [
    selectedAccount,
    connecting,
    requestId,
    selected,
    refetchAccounts,
    onClose,
    navigate,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: connecting,
        onClick: () => onClose(),
      },
      {
        title: 'Connect',
        type: 'primary',
        loading: connecting,
        disabled: !Object.entries(selected).some((a) => a[1]),
        onClick: () => connect(),
      },
    ],
    [connecting, selected, onClose, connect],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      title={`Connect ${capitalize(provider)} Ads Accounts`}
    >
      {provider === 'google' ? (
        <p className="mt-1 text-sm text-gray-500">
          This might take 1-2 minutes
        </p>
      ) : (
        <></>
      )}
      <div className="-mx-6 mt-0 flex flex-col gap-4">
        <ul role="list" className="divide-y divide-gray-100">
          {accounts?.map((account) => (
            <li
              key={account.externalId}
              className={classNames(
                'flex items-center justify-between gap-x-6 px-6 py-5',
                account.status === 'draft'
                  ? 'cursor-pointer hover:bg-gray-50'
                  : 'bg-gray-50',
              )}
              onClick={() => {
                if (account.status !== 'draft') return;
                setSelected((selected) => ({
                  ...selected,
                  [account.externalId]: !selected[account.externalId],
                }));
              }}
            >
              <div className="min-w-0">
                <div className="flex items-start gap-x-3">
                  <p className="text-sm leading-6 font-semibold text-gray-900">
                    {account.name || `${capitalize(provider)} Ads Account`}
                  </p>
                  {'isManagerAccount' in account && account.isManagerAccount ? (
                    <p
                      className={classNames(
                        'bg-green-50 text-green-700 ring-green-600/20',
                        'mt-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset',
                      )}
                    >
                      Manager
                    </p>
                  ) : (
                    <></>
                  )}
                  {'isTestAccount' in account && account.isTestAccount ? (
                    <p
                      className={classNames(
                        'bg-gray-50 text-gray-600 ring-gray-500/10',
                        'mt-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset',
                      )}
                    >
                      Test Account
                    </p>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                  <p className="whitespace-nowrap">
                    {capitalize(provider)} Ads Account
                  </p>
                  <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                    <circle cx={1} cy={1} r={1} />
                  </svg>
                  <p className="whitespace-nowrap">{account.externalId}</p>
                </div>
              </div>

              {account.status === 'draft' ? (
                <div className="mx-2 flex flex-none items-center gap-x-4">
                  <Checkbox
                    disabled={connecting}
                    checked={selected[account.externalId]}
                    onCheckedChange={(checked) =>
                      setSelected((selected) => ({
                        ...selected,
                        [account.externalId]: checked as boolean,
                      }))
                    }
                  />
                </div>
              ) : (
                <></>
              )}
              {account.status === 'active' ? (
                <span className="me-1 mt-1 self-start text-xs whitespace-nowrap text-green-500">
                  Already Connected
                </span>
              ) : (
                <></>
              )}
            </li>
          ))}

          {!accounts?.length ? (
            [0, 1, 2].map((val) => (
              <li
                key={'loading-' + val}
                className="flex animate-pulse items-center justify-between gap-x-6 px-6 py-5"
              >
                <div className="min-w-0">
                  <div className="flex items-start gap-x-3">
                    <span className="mb-1 h-2.5 w-28 rounded-full bg-gray-300"></span>
                  </div>
                  <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                    <span className="h-2 w-24 rounded-full bg-gray-300"></span>
                    <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 fill-current">
                      <circle cx={1} cy={1} r={1} />
                    </svg>
                    <span className="h-2 w-24 rounded-full bg-gray-300"></span>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <></>
          )}
        </ul>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create workspace'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
